import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, MusicNote, MusicNotes, Waveform, Download, Sparkle, Warning, Hourglass } from "@phosphor-icons/react";

const STEMS = [
  { key: "vocals", label: "Voces",     icon: Microphone,  color: "#FF4B00" },
  { key: "drums",  label: "Batería",   icon: MusicNote,   color: "#FFB800" },
  { key: "bass",   label: "Bajo",      icon: MusicNotes,  color: "#B4F34A" },
  { key: "other",  label: "Melodía",   icon: Waveform,    color: "#7FD7FF" },
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Stem separation section. Shown inside the SongDetail page.
 * - Shows the user's stem_credits balance
 * - "Separar pistas" → spends 1 credit and polls until ready
 * - When ready → 4 audio players + WAV download buttons
 * - Failed jobs auto-refund the credit; the section shows the error
 */
export function StemsSection({ songId, songTitle, audioReady }) {
  const [job, setJob] = useState(null);            // {status, stems, error}
  const [credits, setCredits] = useState(0);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!audioReady) return;
    loadStatus();
    loadBalance();
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songId, audioReady]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get(`/stems/${songId}`);
      setJob(data);
      if (data.status === "running") schedulePoll();
    } catch (e) {
      console.warn("loadStatus failed:", e);
    }
  };

  const loadBalance = async () => {
    try {
      const { data } = await api.get("/stems/balance");
      setCredits(data.credits || 0);
    } catch (e) {
      console.warn("loadBalance failed:", e);
    }
  };

  const schedulePoll = () => {
    clearTimeout(pollRef.current);
    pollRef.current = setTimeout(loadStatus, 6000);
  };

  const separate = async () => {
    setStarting(true);
    try {
      await api.post(`/stems/${songId}/separate`);
      toast.success("Separación iniciada · llega en 1-2 minutos");
      await loadBalance();
      schedulePoll();
      // Optimistic state so the loader appears immediately
      setJob({ status: "running", stems: {} });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error iniciando separación";
      toast.error(msg);
      if (e?.response?.status === 402) {
        // No credits → take them to the buy page
        window.location.href = "/stems/buy";
      }
    } finally { setStarting(false); }
  };

  if (!audioReady) return null;

  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#0a0a0a] to-[#0f0a14] border border-[#7FD7FF]/30 p-5" data-testid="stems-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-[#7FD7FF]/15 flex items-center justify-center shrink-0">
          <Waveform weight="duotone" size={22} className="text-[#7FD7FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-lg leading-tight">Separar pistas (stems)</p>
          <p className="text-xs text-white/55 mt-0.5 leading-relaxed">
            Obtén 4 pistas separadas para mezclar en tu DAW: <span className="text-white">voces, batería, bajo y melodía</span>.
          </p>
        </div>
      </div>

      {/* STATES */}
      {job?.status === "ready" ? (
        <ReadyView job={job} songId={songId} songTitle={songTitle} />
      ) : job?.status === "running" ? (
        <RunningView />
      ) : job?.status === "failed" ? (
        <FailedView job={job} credits={credits} onRetry={separate} starting={starting} />
      ) : (
        <IdleView credits={credits} onStart={separate} starting={starting} />
      )}
    </div>
  );
}

function IdleView({ credits, onStart, starting }) {
  return (
    <>
      <div className="rounded-xl bg-black/40 border border-white/10 p-3 mb-3 flex items-center gap-3 flex-wrap">
        <Hourglass weight="fill" size={16} className="text-[#7FD7FF]" />
        <span className="text-sm text-white/65">Tienes</span>
        <span className="font-display font-black text-xl text-[#7FD7FF]" data-testid="stems-credits-balance">{credits}</span>
        <span className="text-sm text-white/65">separación{credits === 1 ? "" : "es"} disponible{credits === 1 ? "" : "s"}</span>
        <Link to="/stems/buy" className="ml-auto text-xs text-white/40 hover:text-white underline" data-testid="stems-buy-link">
          Comprar más
        </Link>
      </div>
      <button
        onClick={onStart}
        disabled={starting}
        className="w-full py-3.5 rounded-full bg-[#7FD7FF] hover:bg-[#5FC0EE] disabled:opacity-40 text-black font-bold flex items-center justify-center gap-2 active:scale-95 transition-all"
        data-testid="stems-separate-btn"
      >
        <Sparkle weight="fill" size={16} />
        {starting ? "Iniciando…" : credits > 0 ? "Separar pistas (1 crédito)" : "Comprar separación · $49 MXN"}
      </button>
      <p className="text-[10px] text-white/40 text-center mt-2">Tarda 1-2 minutos · powered by Demucs</p>
    </>
  );
}

function RunningView() {
  return (
    <div className="rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20 p-5 text-center" data-testid="stems-running">
      <div className="w-12 h-12 rounded-full bg-[#FFB800]/15 mx-auto mb-3 flex items-center justify-center animate-pulse">
        <Waveform weight="fill" size={22} className="text-[#FFB800]" />
      </div>
      <p className="font-display font-bold text-[#FFB800]">Separando tu canción…</p>
      <p className="text-xs text-white/55 mt-1">La IA está aislando voces, batería, bajo y melodía. Tarda 1-2 minutos.</p>
    </div>
  );
}

function FailedView({ job, credits, onRetry, starting }) {
  return (
    <div className="rounded-xl bg-[#FF4B00]/5 border border-[#FF4B00]/20 p-4" data-testid="stems-failed">
      <div className="flex items-start gap-2 mb-2">
        <Warning weight="fill" size={18} className="text-[#FF4B00] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-[#FF4B00]">No se pudo separar</p>
          <p className="text-xs text-white/60 mt-1">{job.error || "Error desconocido"}</p>
          <p className="text-[10px] text-white/40 mt-1">Tu crédito fue reembolsado · Tienes {credits} disponible{credits === 1 ? "" : "s"}.</p>
        </div>
      </div>
      <button onClick={onRetry} disabled={starting} className="mt-2 w-full py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm disabled:opacity-40" data-testid="stems-retry">
        Intentar de nuevo
      </button>
    </div>
  );
}

function ReadyView({ job, songId, songTitle }) {
  return (
    <div className="space-y-2" data-testid="stems-ready">
      {STEMS.map(s => {
        const url = job.stems?.[s.key];
        if (!url) return null;
        const fullUrl = `${BACKEND}${url}`;
        const fileName = `${(songTitle || "song").replace(/[^\w\s-]/g, "").slice(0, 40)} - ${s.label}${url.endsWith("vocals") || url.endsWith("drums") || url.endsWith("bass") || url.endsWith("other") ? ".mp3" : ".wav"}`;
        return (
          <div key={s.key} className="rounded-xl bg-black/40 border border-white/10 p-3" data-testid={`stem-${s.key}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.color}20` }}>
                <s.icon weight="fill" size={18} style={{ color: s.color }} />
              </div>
              <p className="font-semibold flex-1 text-sm">{s.label}</p>
              <a
                href={fullUrl}
                download={fileName}
                className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-xs font-semibold flex items-center gap-1"
                data-testid={`stem-download-${s.key}`}
              >
                <Download size={12} weight="bold" /> Descargar
              </a>
            </div>
            <audio controls src={fullUrl} className="w-full" style={{ height: "30px" }} />
          </div>
        );
      })}
      <p className="text-[10px] text-white/40 text-center mt-2">Pistas separadas con IA · arrastra al DAW de tu preferencia</p>
    </div>
  );
}
